<template>
    <div class="root">
        <section class="filter">
            <div class="route-type-selector">
                <div class="field has-addons">
                    <p class="control" v-for="type in routeTypes" :key="type.id">
                        <button
                            class="button is-small"
                            @click="setRouteType(type.id)"
                            :class="{ 'is-active': type.id === selectedRouteType }"
                        >
                            <span
                                v-if="hasSearchQuery"
                                class="search-result-count tag is-dark is-small"
                            >
                                {{ filteredCount(type.id) }}
                            </span>
                            <span v-else class="icon is-small">
                                <i :class="type.icon"></i>
                            </span>
                            <span :class="{ 'selected-label': type.id === selectedRouteType }">
                                {{ type.label }}
                            </span>
                        </button>
                    </p>
                </div>
            </div>
            <div class="route-text-filter">
                <div class="field">
                    <p class="control has-icons-right">
                        <input
                            @input="updateRoutesSearchQuery"
                            class="input is-small"
                            type="text"
                            placeholder="Search (minimum 3 characters)"
                        />
                        <span class="icon is-small is-right">
                            <i class="fas fa-search"></i>
                        </span>
                    </p>
                </div>
            </div>
        </section>
        <section class="routes">
            <div class="columns severity high">
                <div v-for="route in highSeverityRoutes" :key="route.id" class="column">
                    <RouteCard :route="route" :openRoute="openRoute" />
                </div>
            </div>
            <div class="columns severity low">
                <div v-for="route in lowSeverityRoutes" :key="route.id" class="column">
                    <RouteCard :route="route" :openRoute="openRoute" />
                </div>
            </div>
            <div class="columns severity none">
                <div v-for="route in noSeverityRoutes" :key="route.id" class="column">
                    <RouteCard :route="route" :openRoute="openRoute" />
                </div>
            </div>
        </section>
    </div>
</template>
<script>
import { makeSearcher } from '@/utils/searcher'
import RouteCard from '@/widgets/RouteCard'
import { updateDisplayedRoutes } from '@/views/Dashboard/functions'

const routeSearchPredicate = (query) => {
    const searcher = makeSearcher(query)
    return (route) =>
        searcher.evaluate(
            String(route.id),
            route.courier,
            route.driver.name,
            route.terminalCode,
            String(route.driver.id),
        )
}

export default {
    name: 'Distribution',
    data: () => ({
        routesSearchQuery: '',
        routeTypes: [
            { id: 'DISTRIBUTION', label: 'Home', icon: 'fas fa-home', tabIndex: 0 },
            { id: 'LOCKER', label: 'Box', icon: 'fas fa-door-closed', tabIndex: 1 },
        ],
        selectedRouteType: 'DISTRIBUTION',
    }),
    components: {
        RouteCard,
    },
    props: {
        routes: Array,
        selectedTerminals: Array,
        openRoute: Function,
    },
    computed: {
        hasSearchQuery() {
            const { routesSearchQuery } = this
            if (!routesSearchQuery) {
                return false
            }
            if (/^\s+$/.test(routesSearchQuery)) {
                return false
            }
            return routesSearchQuery.length >= 3
        },
        highSeverityRoutes() {
            const { routes, selectedTerminals, selectedRouteType } = this
            return this.applyRouteFilter(
                updateDisplayedRoutes(routes, selectedTerminals, selectedRouteType)[1],
            )
        },
        lowSeverityRoutes() {
            const { routes, selectedTerminals, selectedRouteType } = this
            return this.applyRouteFilter(
                updateDisplayedRoutes(routes, selectedTerminals, selectedRouteType)[0],
            )
        },
        noSeverityRoutes() {
            const { routes, selectedTerminals, selectedRouteType } = this
            return this.applyRouteFilter(
                updateDisplayedRoutes(routes, selectedTerminals, selectedRouteType)[2],
            )
        },
    },
    methods: {
        switchRouteType() {
            const { routeTypes, selectedRouteType } = this
            const currentTabIndex = routeTypes
                .filter((type) => type.id === selectedRouteType)
                .map((type) => type.tabIndex)[0]

            const nextRouteByTab = routeTypes
                .filter((type) => type.tabIndex === currentTabIndex + 1)
                .map((type) => type.id)[0]

            this.setRouteType(nextRouteByTab || 'DISTRIBUTION')
        },
        filteredCount(routeType) {
            const { routes, selectedTerminals, routesSearchQuery } = this
            if (!routesSearchQuery) {
                return null
            }
            return this.applyRouteFilter(
                updateDisplayedRoutes(routes, selectedTerminals, routeType).flat(),
            ).length
        },
        setRouteType(type) {
            this.selectedRouteType = type
        },
        updateRoutesSearchQuery(e) {
            this.routesSearchQuery = e.target.value.toLowerCase()
            const { selectedRouteType, routeTypes } = this
            if (this.filteredCount(selectedRouteType) !== 0) {
                return
            }
            const typeWithMatches = routeTypes.find((type) => this.filteredCount(type.id) !== 0)
            if (typeWithMatches) {
                this.setRouteType(typeWithMatches.id)
            }
        },
        applyRouteFilter(routes) {
            if (this.routesSearchQuery && this.routesSearchQuery.length > 2) {
                return routes.filter(routeSearchPredicate(this.routesSearchQuery))
            }

            return routes
        },
        interceptKeydown(e) {
            if (e.keyCode === 9) {
                this.switchRouteType()
                e.preventDefault()
            }
        },
    },
    mounted() {
        document.addEventListener('keydown', this.interceptKeydown)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.interceptKeydown)
    },
}
</script>
<style lang="scss" scoped>
.root {
    padding-left: 20px;
    padding-right: 20px;
}

.filter {
    margin: 20px 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
}

.route-type-selector {
    margin-right: 20px;
}

.route-text-filter {
    min-width: 700px;
}

.selected-label {
    font-weight: bold;
}

.search-result-count {
    margin-right: 5px;
}

.severity {
    flex-wrap: wrap;

    .column {
        min-width: 240px;
        max-width: 240px;
    }
}
</style>
